import { useState, useCallback } from 'react';
import { getStoredVKUsers } from '../api/apiUtils';

export const useVKUsers = () => {
  const [vkUsers, setVkUsers] = useState({});
  const [loadingUsers, setLoadingUsers] = useState(new Set());
  const [errorUsers, setErrorUsers] = useState(new Set());

  const fetchUsers = useCallback(async (userIds) => {
    if (!userIds || userIds.length === 0) return {};

    // Фильтруем только те ID, которые еще не загружены и не загружаются
    const uniqueUserIds = Array.from(new Set(userIds.map(id => String(id))));
    const newUserIds = uniqueUserIds.filter(id => 
      !vkUsers[id] && !loadingUsers.has(id)
    );

    // Если все пользователи уже загружены, возвращаем существующие данные
    if (newUserIds.length === 0) {
      const result = {};
      uniqueUserIds.forEach(id => {
        if (vkUsers[id]) {
          result[id] = vkUsers[id];
        }
      });
      return result;
    }

    // Отмечаем пользователей как загружающихся
    setLoadingUsers(prev => new Set([...prev, ...newUserIds]));
    setErrorUsers(prev => {
      const newSet = new Set(prev);
      newUserIds.forEach(id => newSet.delete(id));
      return newSet;
    });

    let loadedUsers = {};

    try {
      const response = await getStoredVKUsers(newUserIds);
      
      if (response && Array.isArray(response)) {
        // Создаем словарь пользователей
        const newVkUsers = {};
        response.forEach(user => {
          if (user && user.id) {
            newVkUsers[String(user.id)] = {
              id: user.id,
              screen_name: user.screen_name || `id${user.id}`,
              first_name: user.first_name || 'Пользователь',
              last_name: user.last_name || `${user.id}`,
              photo_200: user.photo_200 || null,
              full_name: `${user.first_name || 'Пользователь'} ${user.last_name || user.id}`.trim(),
              deactivated: user.deactivated || null
            };
          }
        });

        // Добавляем заглушки для пользователей, которых нет в базе
        newUserIds.forEach(id => {
          if (!newVkUsers[id]) {
            newVkUsers[id] = {
              id: id,
              screen_name: `id${id}`,
              first_name: 'Пользователь',
              last_name: `${id}`,
              photo_200: null,
              full_name: `Пользователь ${id}`,
              deactivated: null
            };
          }
        });

        loadedUsers = newVkUsers;
        setVkUsers(prev => ({ ...prev, ...newVkUsers }));
      } else {
        // Создаем заглушки для всех пользователей
        const newVkUsers = {};
        newUserIds.forEach(id => {
          newVkUsers[id] = {
            id: id,
            screen_name: `id${id}`,
            first_name: 'Пользователь',
            last_name: `${id}`,
            photo_200: null,
            full_name: `Пользователь ${id}`,
            deactivated: null
          };
        });
        loadedUsers = newVkUsers;
        setVkUsers(prev => ({ ...prev, ...newVkUsers }));
      }
    } catch (error) {
      console.error('Ошибка при загрузке пользователей VK:', error);
      
      // Создаем заглушки даже при ошибке
      const newVkUsers = {};
      newUserIds.forEach(id => {
        newVkUsers[id] = {
          id: id,
          screen_name: `id${id}`,
          first_name: 'Пользователь',
          last_name: `${id}`,
          photo_200: null,
          full_name: `Пользователь ${id}`,
          deactivated: null
        };
      });
      loadedUsers = newVkUsers;
      setVkUsers(prev => ({ ...prev, ...newVkUsers }));
      setErrorUsers(prev => new Set([...prev, ...newUserIds]));
    } finally {
      // Убираем пользователей из списка загружающихся
      setLoadingUsers(prev => {
        const newSet = new Set(prev);
        newUserIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }

    // Возвращаем все загруженные данные (новые + существующие)
    const allLoadedUsers = { ...loadedUsers };
    uniqueUserIds.forEach(id => {
      if (vkUsers[id] && !allLoadedUsers[id]) {
        allLoadedUsers[id] = vkUsers[id];
      }
    });

    return allLoadedUsers;
  }, [vkUsers, loadingUsers]);

  const getUserInfo = useCallback((userId) => {
    const id = String(userId);
    return vkUsers[id] || null;
  }, [vkUsers]);

  const isUserLoading = useCallback((userId) => {
    return loadingUsers.has(String(userId));
  }, [loadingUsers]);

  const hasUserError = useCallback((userId) => {
    return errorUsers.has(String(userId));
  }, [errorUsers]);

  const clearUserData = useCallback(() => {
    setVkUsers({});
    setLoadingUsers(new Set());
    setErrorUsers(new Set());
  }, []);

  return {
    vkUsers,
    fetchUsers,
    getUserInfo,
    isUserLoading,
    hasUserError,
    clearUserData
  };
};
